import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { FaCheck } from 'react-icons/fa';
import { CatalogPlan, PlanTier, BillingInterval, parseDecimal } from '../services/BillingService';
import { personalakteStyles as ps, colors } from '../../employees/styles/personalakteStyles';

interface PlanSelectionProps {
  plans: CatalogPlan[];
  currentTier: PlanTier | null;
  selectedTier: PlanTier;
  billingInterval: BillingInterval;
  onSelectPlan: (tier: PlanTier) => void;
  isDesignPartner?: boolean;
  onSelectDesignPartner?: () => void;
}

function PlanCard({
  plan,
  isCurrent,
  isSelected,
  onSelect,
}: {
  plan: CatalogPlan;
  isCurrent: boolean;
  isSelected: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  const [hovered, setHovered] = useState(false);
  const price = parseDecimal(plan.price_monthly);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        background: colors.bgCard,
        borderRadius: 20,
        padding: 0,
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
        transform: hovered ? 'translateY(-4px) scale(1.02)' : 'translateY(0) scale(1)',
        boxShadow: isSelected
          ? '0 8px 32px rgba(231, 0, 125, 0.18), 0 0 0 2px #E7007D'
          : hovered
            ? '0 12px 28px rgba(14, 19, 51, 0.12)'
            : '0 2px 8px rgba(14, 19, 51, 0.06)',
        border: isSelected
          ? '2px solid transparent'
          : `1px solid ${colors.border}`,
        cursor: isSelected ? 'default' : 'pointer',
      }}
      onClick={() => !isSelected && onSelect()}
    >
      <div style={{ padding: '28px 24px 24px', flex: 1, display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
        {/* Plan name */}
        <h3 style={{ fontSize: 22, fontWeight: 800, color: colors.navy, margin: '0 0 16px 0' }}>
          {plan.name}
        </h3>

        {/* Price */}
        <div style={{ marginBottom: 4 }}>
          <span style={{ fontSize: 36, fontWeight: 800, color: colors.navy }}>
            {price.toFixed(0)}€
          </span>
        </div>
        <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 4 }}>
          pro Standort / Monat
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: colors.navy, marginBottom: 20 }}>
          inkl. {plan.included_employees_per_location} aktive Mitarbeitende
        </div>

        {/* CTA Button */}
        <button
          onClick={(e) => { e.stopPropagation(); if (!isSelected) onSelect(); }}
          disabled={isSelected}
          style={{
            width: '100%',
            padding: '12px 24px',
            borderRadius: 9999,
            border: isSelected ? '2px solid #0E1333' : 'none',
            background: isSelected
              ? 'transparent'
              : 'linear-gradient(90deg, #0E1333 0%, #0E1333 40%, #E7007D 100%)',
            color: isSelected ? colors.navy : '#fff',
            fontSize: 14,
            fontWeight: 700,
            cursor: isSelected ? 'default' : 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            marginBottom: 16,
          }}
        >
          {isSelected && <FaCheck size={11} />}
          {isCurrent ? 'Aktueller Plan' : isSelected ? 'Ausgewählt' : 'Paket wählen'}
        </button>

        {/* Features */}
        <div style={{ flex: 1, textAlign: 'left' }}>
          {plan.features.map((feature, idx) => {
            const label = typeof feature === 'string' ? feature : (feature as any).name || '';
            return (
              <div
                key={idx}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '5px 0', fontSize: 13, color: colors.textBody,
                }}
              >
                <FaCheck size={10} color={colors.success} style={{ flexShrink: 0 }} />
                {label}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function PlanSelection({
  plans,
  currentTier,
  selectedTier,
  billingInterval,
  onSelectPlan,
  isDesignPartner,
  onSelectDesignPartner,
}: PlanSelectionProps): React.JSX.Element {
  const handleCompare = () => {
    toast.info('Funktionsvergleich kommt bald.');
  };

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={ps.sectionHeader}>
        <div style={ps.sectionDot(colors.magenta)} />
        Paketauswahl
      </div>

      {isDesignPartner && (
        <div
          style={{
            background: 'linear-gradient(135deg, #0E1333 0%, #E7007D 100%)',
            borderRadius: 20,
            padding: '28px 32px',
            marginBottom: 20,
            marginTop: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 16,
          }}
        >
          <div>
            <h3 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: '0 0 4px 0' }}>
              Premium Designpartner
            </h3>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
              129 € / Standort / Monat — exklusives Angebot für Designpartner
            </div>
          </div>
          <button
            onClick={onSelectDesignPartner}
            style={{
              background: '#fff',
              color: '#0E1333',
              border: 'none',
              borderRadius: 9999,
              padding: '12px 28px',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.03)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Angebot w\u00e4hlen
          </button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 16, paddingTop: isDesignPartner ? 0 : 16 }}>
        {plans.length === 0 && (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px 0', color: '#8094ae', fontSize: 13 }}>
            Pakete konnten nicht geladen werden. Bitte Seite neu laden.
          </div>
        )}
        {plans.map((plan) => (
          <PlanCard
            key={plan.tier}
            plan={plan}
            isCurrent={plan.tier === currentTier}
            isSelected={plan.tier === selectedTier}
            onSelect={() => onSelectPlan(plan.tier)}
          />
        ))}
      </div>

      <div style={{ textAlign: 'center' }}>
        <button
          onClick={handleCompare}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 600, color: colors.magenta, padding: '4px 0',
          }}
        >
          Alle Funktionen vergleichen →
        </button>
      </div>
    </div>
  );
}
